/** Header-action builder for the Messages (DMs) view.
 *  One leading slot (Search, or Back in compose mode); Compose and Filter sit
 *  trailing. Compose moves to LEADING only while its morph is open, because the
 *  expanded morph has to occupy the leading cluster — see the note at its push.
 *  The compose pill opens the morph-input in the main header, so the callbacks
 *  for mutating that state are passed in via ctx rather than called directly.
 *  All other actions fire window events that ChatListPanel listens for. No JSX. */

import { Search, SquarePen, ChevronLeft, X } from '@/shared/ui/icons';
import type { MobileHeaderAction } from './types';

export interface MessagesHeaderState {
  isSearchMode: boolean;
  mode: 'idle' | 'search' | 'compose';
  isComposeMode: boolean;
}

export interface MessagesHeaderCtx {
  messagesHeaderState: MessagesHeaderState;
  messagesComposeOpen: boolean;
  /** Opens the compose morph-input in the parent header. */
  onOpenCompose: () => void;
  /** Dismisses the compose morph entirely (clears value + exits compose mode). */
  onDismissCompose: () => void;
  /** Back replaces the browse/search action while a conversation is open. */
  hideBrowseAction?: boolean;
  /** Transitions the messages header mode (idle / search / compose). */
  setMessagesHeaderMode: (mode: 'idle' | 'search' | 'compose') => void;
  leadingActions: MobileHeaderAction[];
  trailingActions: MobileHeaderAction[];
  pushNotificationsAction: () => void;
}

/** Appends Messages pills to the caller's leading/trailing action arrays.
 *  Called only when `currentView === 'messages'`. */
export function buildMessagesHeaderActions(ctx: MessagesHeaderCtx): void {
  const {
    messagesHeaderState,
    messagesComposeOpen,
    onOpenCompose,
    onDismissCompose,
    hideBrowseAction = false,
    setMessagesHeaderMode,
    leadingActions,
    trailingActions,
    pushNotificationsAction,
  } = ctx;

  // DMs page header: 2-2 split. Search + new-message on the left
  // (browse / start), filter + bell on the right (refine / alerts).
  // While compose morph is open, hide the search pill so the two
  // morphs don't compete for the leading cluster's space.
  if (!messagesComposeOpen && !hideBrowseAction) {
    leadingActions.push({
      id: messagesHeaderState.isComposeMode ? 'messages-back' : 'messages-search',
      icon: messagesHeaderState.isComposeMode ? ChevronLeft : Search,
      ariaLabel: messagesHeaderState.isComposeMode ? 'Back to messages' : 'Search messages',
      onClick: () => {
        if (messagesHeaderState.isComposeMode) {
          setMessagesHeaderMode('idle');
          window.dispatchEvent(new CustomEvent('messages:dismiss-search'));
          return;
        }
        setMessagesHeaderMode(messagesHeaderState.mode === 'search' ? 'idle' : 'search');
        window.dispatchEvent(new CustomEvent(
          messagesHeaderState.mode === 'search' ? 'messages:dismiss-search' : 'messages:toggle-search'
        ));
      },
    });
  }
  // Compose is an action → trailing (top-right) edge per Apple HIG (matches the
  // iOS Messages app); leading stays reserved for search / back.
  // EXCEPT while the compose morph is OPEN: the mobile row hides the title +
  // trailing pills (searchActiveOnMobile takeover) and expects the expanded
  // morph to occupy the LEADING cluster — only leading actions survive the
  // takeover filter. Without this swap the open morph never renders and the
  // compose screen has no recipient input, back affordance, or header at all.
  if (messagesComposeOpen) {
    leadingActions.push({
      id: 'messages-new',
      icon: SquarePen,
      ariaLabel: 'New message',
      onClick: onOpenCompose,
    });
    // Cancel pill survives the takeover filter (mirrors documents-search-cancel)
    // — the morph's in-pill X only clears text, so this is the touch dismissal.
    trailingActions.push({
      id: 'messages-compose-cancel',
      icon: X,
      ariaLabel: 'Cancel new message',
      onClick: onDismissCompose,
      group: 'page',
    });
  } else if (!messagesHeaderState.isComposeMode) {
    // Hidden while composing (mobile in-content compose): the leading back
    // chevron is the exit, and a compose pencil on the compose screen is noise.
    trailingActions.push({
      id: 'messages-new',
      icon: SquarePen,
      ariaLabel: 'New message',
      onClick: onOpenCompose,
      group: 'page',
    });
  }
  pushNotificationsAction();
}
